import { useCallback } from "react";
import { useStore } from "jotai";
import type { SubmitAccessibility } from "common/apis";
import { useAccessibility } from "common/hooks";

import { onboardingV2AnswersAtom } from "./atoms";

/**
 * Turns sign-up's onboarding answers into the `user/register-update` payload -
 * the endpoint the old profile-setup wizard posted to, and the only one that
 * takes a not-yet-verified account by id rather than by token.
 *
 * - **Access need** → the picked group's child tag ids, as
 *   `accessibilityFeatureRequestTagsValue`, which the backend saves as the
 *   account's default accessibility preferences.
 * - **How did you hear** → group ids (or option ids on the fallback list),
 *   plus the free text when the group keeps it.
 * - **User type and audience have nowhere to go on this endpoint.** They are
 *   kept in this browser with the rest of the answers; `POST user/onboarding`
 *   takes them, but needs a signed-in account.
 *
 * Reads the answers from the store at call time rather than from a render, so
 * an answer written in the same click is included.
 */
export function useSignUpProfilePayload() {
  const store = useStore();
  const { mattersToYou } = useAccessibility();

  return useCallback(
    (userId: number): SubmitAccessibility => {
      const { accessNeeds, hearAbout } = store.get(onboardingV2AnswersAtom);

      const tagIds = mattersToYou
        .filter((group) => accessNeeds.includes(group.group_name))
        .flatMap((group) => group.children.map((child) => child.id));

      return {
        userId,
        ability: "",
        accessibility_need_selection: "",
        makeDisabilityVisibleValue: false,
        accessibilityFeatureRequestTagsValue: tagIds.join(","),
        how_do_you_hear_about_us_ids:
          hearAbout?.kind === "option" ? String(hearAbout.id) : "",
        how_do_you_hear_about_us_group_ids:
          hearAbout?.kind === "group" ? String(hearAbout.id) : "",
        how_do_you_hear_option_other_text: hearAbout?.otherText?.trim() ?? "",
      };
    },
    [mattersToYou, store],
  );
}
